import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { medicationsActions, treatmentPlanActions } from '../../../../../store';
import { getAllInvalidFieldNames } from '../../../../../helpers';
import { TreatmentPlanPages } from '../../constants/general';
import { MedicationsRowSchema } from '../../medications/validationSchema';

import type { RootState } from '../../../../../store';
import type { IMedication } from '../../../../../utils/types';

function useValidateMedications() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { selected: selectedClient } = useSelector((state: RootState) => state.clients);

  const { treatmentPlanId: currentTPId, list: medications } = useSelector(
    (state: RootState) => state.medications,
  );

  const hasMedication = treatmentPlan?.has_medication;
  const [isValid, setIsValid] = useState<boolean>(false);
  const [treatmentPlanId, setTreatmentPlanId] = useState<number>();

  useEffect(() => {
    setTreatmentPlanId(treatmentPlan?.id);
  }, [treatmentPlan]);

  useEffect(() => {
    if (!treatmentPlanId || !selectedClient?.id) return;
    if (currentTPId !== treatmentPlanId) {
      dispatch(medicationsActions.clearState());
      dispatch(
        medicationsActions.getList({
          planId: treatmentPlanId,
          getAccessTokenSilently,
        }),
      );
      dispatch(medicationsActions.updateState({ key: 'treatmentPlanId', data: treatmentPlanId }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlanId, selectedClient]);

  useEffect(() => {
    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMedication, medications]);

  function validate() {
    if (!treatmentPlan?.id) return;

    if (
      hasMedication === undefined ||
      hasMedication === null ||
      (hasMedication && medications.length === 0)
    ) {
      setIsValid(false);
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.Medications]: '!',
        }),
      );
    } else if (hasMedication) {
      let errorCount = 0;

      medications.forEach((medication: IMedication) => {
        const medicationFieldsData = { ...medication };
        delete medicationFieldsData.utc_offset;
        delete medicationFieldsData.treatment_plan_id;
        const errorObject = getAllInvalidFieldNames(MedicationsRowSchema, medicationFieldsData);
        errorCount += Object.values(errorObject).filter(Boolean).length;
      });
      setIsValid(errorCount === 0 ? true : false);
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.Medications]: errorCount,
        }),
      );
    } else if (hasMedication === false) {
      setIsValid(true);
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.Medications]: 0,
        }),
      );
    }
  }

  return isValid;
}

export default useValidateMedications;
